import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { css, useTheme } from "styled-components/native";
import Spacer from "@components/atoms/Spacer";
import { SpaceType } from "@components/atoms/Spacer/types";
import { VDBText } from "@components/atoms";
import { useTranslation } from "react-i18next";
import { useTypographyStore } from "@redux/slices/typographySlice";
import currency from "currency.js";
import styled from "styled-components/native";
import { useState } from "react";
import { ShareShortTitleProps, ShareType } from "../types";
import EmptyImagePreview from "@components/molecules/GalleryView/utils/EmptyImage/EmptyImagePreview";
import { getShapeValue } from "@components/molecules/GalleryView/utils/EmptyImage/utils";
import { useUserCurrency } from "../../../../hooks/getCurrency";
import { VDBIcons } from "@utils/VDBIcons";
import { Platform, Image as RNImage } from "react-native";
import { Image as ExpoImage } from "expo-image";
import { isPair } from "@utils/product/productValidators";
import { useUserInfo } from "@redux/slices/userInfoSlice";

const ImageComponent = Platform.OS === "ios" ? RNImage : ExpoImage;

const ShareShortTitle: React.FC<ShareShortTitleProps> = ({
  shareType,
  userInfo,
  mItem,
  collection,
  favoriteCount,
  itemCount,
  totalSalesPrice,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const [imageUriError, setImageUriError] = useState(false);
  const showPrice = userInfo.price_mode === 1 || userInfo.price_mode === 2;
  const userCurrency = useUserCurrency();
  const { disableImageZoom } = useUserInfo();

  const renderIcon = () => {
    if (shareType === ShareType.singleShare) {
      if ((mItem?.image_url || mItem?.image_thumb_url) && !imageUriError) {
        return (
          <ProductImage
            source={{ uri: mItem?.image_url || mItem?.image_thumb_url }}
            onError={() => setImageUriError(true)}
            resizeMode={disableImageZoom() ? "contain" : "cover"}
          />
        );
      } else {
        return (
          <IconContainer>
            <EmptyImagePreview
              shape={getShapeValue(mItem)}
              size={45}
              isPair={isPair(mItem) ?? false}
            />
          </IconContainer>
        );
      }
    } else if (shareType === ShareType.multiShare) {
      return (
        <IconContainer>
          <VDBIcon
            name={VDBIcons.IMAGE}
            size={23}
            color={theme["core-06"]}
            variant={IconsVariant.VDBCustom}
          />
        </IconContainer>
      );
    } else {
      return (
        <IconContainer>
          <VDBIcon
            name={VDBIcons.COLLECTION}
            size={23}
            color={theme["core-04"]}
            variant={IconsVariant.VDBCustom}
          />
        </IconContainer>
      );
    }
  };

  const renderTitle = () => {
    if (
      shareType === ShareType.singleShare ||
      shareType === ShareType.multiShare
    ) {
      return (
        <Title showPrice={showPrice}>
          <VDBText
            type={typography["Minor-M "]}
            color={theme["core-05"]}
            allowFontScaling={true}
          >
            {shareType === ShareType.singleShare
              ? mItem?.short_title
              : shareType === ShareType.multiShare
              ? `${itemCount} ${t("shareScreen.bulk.itemSelected.text")}`
              : null}
          </VDBText>
        </Title>
      );
    } else if (shareType === ShareType.collections) {
      return (
        <Title showPrice={true}>
          <VDBText type={typography["Minor-M "]} color={theme["core-05"]}>
            {collection?.name}
          </VDBText>
        </Title>
      );
    } else {
      return (
        <Title showPrice={true}>
          <VDBText type={typography["Minor-M "]} color={theme["core-05"]}>
            {t("shareScreen.favorites.title.text")}
          </VDBText>
        </Title>
      );
    }
  };

  const renderPrice = () => {
    if (
      (shareType === ShareType.singleShare ||
        shareType === ShareType.multiShare) &&
      showPrice
    ) {
      const getTotalSalesPrice = () => {
        if (shareType === ShareType.singleShare) {
          return mItem?.total_sales_price ?? 0;
        } else if (shareType === ShareType.multiShare) {
          return totalSalesPrice ?? 0;
        } else {
          return 0;
        }
      };
      return (
        <>
          <Spacer size={SpaceType.ExtraSmall} />
          <Price>
            <VDBText type={typography["Minor-R "]} color={theme["core-06"]}>
              {shareType === ShareType.singleShare
                ? `${t("shareScreen.single.price.text")}`
                : shareType === ShareType.multiShare
                ? `${t("shareScreen.bulk.totalSales.text")}`
                : null}
            </VDBText>
            <Spacer size={SpaceType.ExtraSmall} />
            <VDBText type={typography["Minor-M "]} color={theme["core-05"]}>
              {currency(getTotalSalesPrice(), {
                symbol: userCurrency,
                precision: 2,
              }).format()}
            </VDBText>
          </Price>
        </>
      );
    }
  };

  const renderItemCount = () => {
    if (
      shareType === ShareType.collections ||
      shareType === ShareType.favorites
    ) {
      return (
        <>
          <Spacer size={SpaceType.ExtraSmall} />
          <VDBText type={typography["Minor-R "]} color={theme["core-06"]}>
            {shareType === ShareType.favorites && favoriteCount}
            {collection?.count}{" "}
            {(collection?.count ?? favoriteCount ?? 0) > 1
              ? `${t("shareScreen.collections.items.text")}`
              : `${t("shareScreen.collections.item.text")}`}
          </VDBText>
        </>
      );
    }
  };

  return (
    <>
      <Overlay>
        {renderIcon()}

        <Spacer size={SpaceType.Basic} />
        <TextContainer>
          {renderTitle()}
          {renderPrice()}
          {renderItemCount()}
        </TextContainer>
      </Overlay>
      <Spacer size={SpaceType.Big} />
    </>
  );
};

const Overlay = styled.View`
  align-items: center;
  padding: ${SpaceType.Basic}px;
  flex-direction: row;
  border-radius: 8px;
  background-color: ${({ theme }) => theme["core-01"]};
  overflow: hidden;
`;

const ProductImage = styled(ImageComponent)`
  width: 48px;
  height: 48px;
  border-radius: 8px;
`;

const Title = styled.View<{
  showPrice?: boolean;
}>`
  padding-top: ${({ showPrice }) => (showPrice ? `2px` : `11px`)};
  overflow: hidden;
  max-width: 300px;
  ${({ theme }) =>
    theme.isMobile &&
    css`
      max-width: 300px;
    `}
`;

const TextContainer = styled.View`
  flex-direction: column;
  padding-top: ${SpaceType.ExtraSmall}px;
  padding-bottom: ${SpaceType.ExtraSmall}px;
  overflow: hidden;
`;

const Price = styled.View`
  flex-direction: row;
`;
const IconContainer = styled.View`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme["core-02"]};
  justify-content: center;
  align-items: center;
`;

export default ShareShortTitle;
